/*
 * Tokens de movimiento para JavaScript (Web Animations API, contadores).
 * La fuente de verdad es tokens.css: en el navegador se lee la custom property, así que con
 * prefers-reduced-motion las duraciones llegan a 0 sin código extra. Los valores de aquí son el
 * respaldo cuando no hay CSS (tests en jsdom) y deben coincidir con tokens.css.
 */

export const DURATION = {
  instant: 90,
  fast: 160,
  base: 240,
  slow: 420,
  station: 640,
  count: 1200,
  stagger: 70,
} as const;

export type DurationToken = keyof typeof DURATION;

export const EASING = {
  standard: 'cubic-bezier(0.2, 0, 0, 1)',
  enter: 'cubic-bezier(0, 0, 0.2, 1)',
  exit: 'cubic-bezier(0.4, 0, 1, 1)',
  rail: 'cubic-bezier(0.65, 0, 0.35, 1)',
  stop: 'cubic-bezier(0.3, 1.3, 0.5, 1)',
} as const;

export type EasingToken = keyof typeof EASING;

function cssVar(name: string, el?: Element | null): string {
  if (typeof window === 'undefined' || typeof getComputedStyle !== 'function') return '';
  const target = el ?? document.documentElement;
  return getComputedStyle(target).getPropertyValue(name).trim();
}

/** Duración en milisegundos del token (`--duration-*` o `--stagger`). */
export function readDuration(token: DurationToken, el?: Element | null): number {
  const raw = cssVar(token === 'stagger' ? '--stagger' : `--duration-${token}`, el);
  const match = /^(-?\d*\.?\d+)(ms|s)$/.exec(raw);
  if (!match) return DURATION[token];
  const value = Number(match[1]);
  return match[2] === 's' ? value * 1000 : value;
}

/** Curva del token (`--ease-*`) como cadena válida para WAAPI. */
export function readEasing(token: EasingToken, el?: Element | null): string {
  return cssVar(`--ease-${token}`, el) || EASING[token];
}

/** true si el elemento soporta la Web Animations API (jsdom no la tiene). */
export function canAnimate(el: Element): boolean {
  return typeof (el as Partial<Element>).animate === 'function';
}
